/**
 * Analyst Co-Pilot API service.
 *
 * The read-only data Analyst, reached through a profile-LOCKED door at
 * `/api/analyst-copilot` (the backend forces the `analyst_portal` profile — the
 * un-re-skinned variant of the console `analyst`, so it is the SAME agent, just a
 * focused portal UI). This is a TEXT-only client: it reuses the Agent Console SSE
 * wire contract + pure parsing helpers, pointed at the analyst-copilot endpoints.
 * No voice channel, no mail panel.
 *
 * It deliberately reuses agentConsole.ts's pure parsing helpers + types rather
 * than duplicating the SSE plumbing.
 */
import api from './api';
import {
  CONSOLE_DOOR_REQUEST_TIMEOUT_MS,
  streamConsoleSse,
  type ConsoleEvent,
  type ConversationCreate,
  type ConversationDetail,
  type ConversationRead,
  type ModelsResponse,
} from './agentConsole';

export type {
  ConsoleEvent,
  ConversationCreate,
  ConversationDetail,
  ConversationRead,
  ModelOption,
  ModelsResponse,
} from './agentConsole';

const BASE = '/analyst-copilot';

export async function createConversation(
  body: ConversationCreate = {},
): Promise<ConversationRead> {
  const { data } = await api.post<ConversationRead>(`${BASE}/conversations`, body, {
    timeout: CONSOLE_DOOR_REQUEST_TIMEOUT_MS,
  });
  return data;
}

/** Selectable models + this door's (Analyst) default model, for the picker. */
export async function getModels(): Promise<ModelsResponse> {
  const { data } = await api.get<ModelsResponse>(`${BASE}/models`);
  return data;
}

export async function listConversations(
  opts: { limit?: number; offset?: number } = {},
): Promise<ConversationRead[]> {
  const params: Record<string, number> = {};
  if (opts.limit != null) params.limit = opts.limit;
  if (opts.offset != null) params.offset = opts.offset;
  const { data } = await api.get<{ items: ConversationRead[] }>(
    `${BASE}/conversations`,
    { params },
  );
  return data.items;
}

export async function getConversationDetail(
  conversationId: string,
): Promise<ConversationDetail> {
  const { data } = await api.get<ConversationDetail>(
    `${BASE}/conversations/${conversationId}`,
    { timeout: CONSOLE_DOOR_REQUEST_TIMEOUT_MS },
  );
  return data;
}

/**
 * Open the SSE stream for one Analyst turn. Invokes `onEvent` for every parsed
 * frame; resolves when the server closes the stream (after `done`). Throws only
 * on a non-OK HTTP response — turn-level failures arrive as `error` frames. The
 * profile is forced server-side, so no `profile_id` is sent.
 */
export async function streamAnalystMessage(
  conversationId: string,
  prompt: string,
  onEvent: (evt: ConsoleEvent) => void,
  signal?: AbortSignal,
  model?: string,
): Promise<void> {
  // Carry the selected model (last-turn-wins, persisted server-side); omitted ->
  // keep the conversation's current model. The profile stays forced server-side,
  // so no profile_id is sent. The shared loop handles the 401 refresh-and-retry.
  const payload: { prompt: string; model?: string } = { prompt };
  if (model) payload.model = model;
  return streamConsoleSse(
    `/api/analyst-copilot/conversations/${conversationId}/message`,
    payload,
    onEvent,
    signal,
  );
}
